/**
 * Login Notification Service
 * Sends login notifications to cofounder via EmailJS
 */

import emailjs from '@emailjs/browser';

// EmailJS configuration - using same credentials as contact form
const EMAILJS_SERVICE_ID = 'service_frjhf5v';
const EMAILJS_TEMPLATE_ID = 'template_xfw95pa'; // We'll use the same template for now
const EMAILJS_PUBLIC_KEY = 'f9kwaPX-7NUpPMR6_';

/**
 * Send login notification email to cofounder
 * @param {Object} loginData - Login information
 * @param {string} loginData.email - User's email
 * @param {string} loginData.name - User's name
 * @param {string} loginData.phone - User's phone number
 * @param {string} loginData.investorType - Type of investor/visitor
 * @param {string} loginData.loginMethod - Login method (google, email, phone)
 * @param {string} loginData.timestamp - Login timestamp
 */
export const sendLoginNotification = async (loginData) => {
  try {
    console.log('🚀 [LOGIN NOTIFICATION] Starting login notification process...');
    console.log('📧 [LOGIN NOTIFICATION] User data:', {
      email: loginData.email,
      name: loginData.name,
      phone: loginData.phone,
      investorType: loginData.investorType,
      loginMethod: loginData.loginMethod,
      timestamp: loginData.timestamp
    });
    
    // Validate required data
    if (!loginData.email || !loginData.investorType) {
      console.error('❌ [LOGIN NOTIFICATION] Missing required data:', {
        hasEmail: !!loginData.email,
        hasInvestorType: !!loginData.investorType
      });
      throw new Error('Missing required login data');
    }

    // Initialize EmailJS
    emailjs.init(EMAILJS_PUBLIC_KEY);
    console.log('🔧 [LOGIN NOTIFICATION] EmailJS initialized with public key');

    const templateParams = {
      name: loginData.name || 'Unknown User',           // Add this line
      from_name: loginData.name || 'Unknown User',
      from_email: loginData.email,
      from_phone: loginData.phone || 'Not provided',
      subject: `New User Login - ${loginData.investorType || 'Unknown Type'}`,
      message: `User logged in via ${loginData.loginMethod || 'Unknown'} method`,
      investor_type: loginData.investorType || 'Not specified',
      to_email: 'patrick@honestlee.app',
      email: loginData.email,                           // Add this line
      reply_to: loginData.email,
      timestamp: loginData.timestamp || new Date().toLocaleString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    };

    console.log('📝 [LOGIN NOTIFICATION] Template parameters:', templateParams);
    console.log('📤 [LOGIN NOTIFICATION] Sending email via EmailJS...');

    let response;
    try {
      response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('📨 [LOGIN NOTIFICATION] EmailJS response:', response);
      console.log('📊 [LOGIN NOTIFICATION] Response status:', response.status);
      console.log('📊 [LOGIN NOTIFICATION] Response text:', response.text);
      
    } catch (emailError) {
      console.error('❌ [LOGIN NOTIFICATION] EmailJS Send Error:', emailError);
      console.error('❌ [LOGIN NOTIFICATION] Error details:', {
        message: emailError.message,
        status: emailError.status,
        text: emailError.text,
        stack: emailError.stack
      });
      throw emailError;
    }

    if (response.status === 200) {
      console.log('✅ [LOGIN NOTIFICATION] Login notification sent successfully to patrick@honestlee.app');
      console.log('📊 [LOGIN NOTIFICATION] Response details:', {
        status: response.status,
        text: response.text,
        timestamp: new Date().toISOString()
      });
      return {
        success: true,
        message: 'Login notification sent',
        timestamp: new Date().toISOString()
      };
    } else {
      console.error('❌ [LOGIN NOTIFICATION] EmailJS returned non-200 status:', response.status);
      throw new Error(`Failed to send login notification. Status: ${response.status}`);
    }

  } catch (error) {
    console.error('❌ [LOGIN NOTIFICATION] Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    console.error('❌ [LOGIN NOTIFICATION] Full error object:', error);
    return {
      success: false,
      error: error.message || 'Failed to send login notification'
    };
  }
};

/**
 * Test EmailJS connection for login notifications
 */
export const testLoginNotificationConnection = async () => {
  try {
    console.log('🧪 [TEST] Starting EmailJS connection test...');
    console.log('🧪 [TEST] Service ID:', EMAILJS_SERVICE_ID);
    console.log('🧪 [TEST] Template ID:', EMAILJS_TEMPLATE_ID);
    
    emailjs.init(EMAILJS_PUBLIC_KEY);
    console.log('🧪 [TEST] EmailJS initialized');
    
    const testParams = {
      from_name: 'Test User',
      from_email: 'test@example.com',
      subject: 'Test Login Notification',
      message: 'This is a test login notification',
      investor_type: 'Individual Investor',
      timestamp: new Date().toLocaleString(),
      user_agent: 'Test Agent',
      page_url: 'https://test.com',
      to_email: 'patrick@honestlee.app'
    };

    console.log('🧪 [TEST] Sending test email with params:', testParams);
    
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      testParams
    );

    console.log('🧪 [TEST] Test response:', response);
    
    return {
      success: response.status === 200,
      message: response.status === 200 ? 'Connection successful' : 'Connection failed',
      response: response
    };
  } catch (error) {
    console.error('🧪 [TEST] Test failed:', error);
    return {
      success: false,
      message: error.message || 'Connection test failed',
      error: error
    };
  }
};

// Make test function available globally for console testing
if (typeof window !== 'undefined') {
  window.testLoginNotification = testLoginNotificationConnection;
}
